import { MOSOption, BiologicalSex } from '../types';

const API_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:3002'
  : '';

export interface SessionData {
  id: string;
  session_code: string;
  last_name: string | null;
  sex: string | null;
  selected_mos_id: string | null;
  selected_mos_title: string | null;
  original_photo: string | null;
  portrait_photo_url: string | null;
  field_photo_url: string | null;
  mos_images: Record<string, { portrait: string; field: string }> | null;
  email: string | null;
  status: string;
  created_at: string;
  completed_at: string | null;
}

export interface CreateSessionResponse {
  success: boolean;
  sessionId?: string;
  sessionCode?: string;
  error?: string;
}

export interface GetSessionResponse {
  success: boolean;
  session?: SessionData;
  error?: string;
}

export interface UploadImagesResponse {
  success: boolean;
  portraitUrl?: string;
  fieldUrl?: string;
  error?: string;
}

export async function createSession(initialData?: {
  lastName?: string;
  sex?: BiologicalSex;
  selectedMOS?: MOSOption;
}): Promise<CreateSessionResponse> {
  try {
    const response = await fetch(`${API_BASE}/api/session/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(initialData || {}),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Create session error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create session',
    };
  }
}

export async function getSession(sessionId: string): Promise<GetSessionResponse> {
  try {
    const response = await fetch(`${API_BASE}/api/session-by-id?sessionId=${sessionId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Get session error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get session',
    };
  }
}

export async function updateSession(
  sessionId: string,
  updateData: {
    lastName?: string;
    sex?: BiologicalSex;
    selectedMOS?: MOSOption;
    originalPhoto?: string;
    portraitPhotoUrl?: string;
    fieldPhotoUrl?: string;
    status?: string;
    errorMessage?: string;
  }
): Promise<GetSessionResponse> {
  try {
    const response = await fetch(`${API_BASE}/api/session-by-id?sessionId=${sessionId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Update session error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update session',
    };
  }
}

// Helper to compress and convert base64 to smaller JPEG
async function compressImage(base64Data: string, quality: number = 0.75): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob'));
          }
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = base64Data;
  });
}

// Upload a single image using presigned URL (bypasses Vercel payload limits)
async function uploadImageDirect(
  sessionId: string,
  imageBase64: string,
  imageType: 'portrait' | 'field',
  mosId?: string
): Promise<string | null> {
  try {
    // Step 1: Get presigned URL from our API
    const urlResponse = await fetch(`${API_BASE}/api/get-upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sessionId,
        mosId,
        imageType,
      }),
    });

    if (!urlResponse.ok) {
      console.error(`Failed to get upload URL: ${urlResponse.status}`);
      return null;
    }

    const { uploadUrl, publicUrl } = await urlResponse.json();

    // Step 2: Compress and upload directly to R2 using presigned URL
    const blob = await compressImage(imageBase64, 0.80);
    console.log(`Uploading ${imageType} directly to R2 (${blob.size} bytes, compressed)...`);

    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'image/jpeg',
      },
      body: blob,
    });

    if (!uploadResponse.ok) {
      console.error(`Direct upload failed: ${uploadResponse.status}`);
      return null;
    }

    console.log(`${imageType} uploaded successfully: ${publicUrl}`);
    return publicUrl;
  } catch (error) {
    console.error(`Direct upload error for ${imageType}:`, error);
    return null;
  }
}

// Fallback: Upload via server with compressed images
async function uploadImageViaServer(
  sessionId: string,
  portraitImage: string,
  fieldImage: string,
  mosId?: string
): Promise<UploadImagesResponse> {
  try {
    // Compress images to reduce payload size (target ~1-2MB each)
    console.log('Compressing images for server upload...');
    const [portraitBlob, fieldBlob] = await Promise.all([
      compressImage(portraitImage, 0.60),
      compressImage(fieldImage, 0.60),
    ]);

    // Convert blobs back to base64
    const toBase64 = (blob: Blob): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    };

    const [portraitBase64, fieldBase64] = await Promise.all([
      toBase64(portraitBlob),
      toBase64(fieldBlob),
    ]);

    console.log(`Compressed sizes: portrait=${portraitBlob.size}, field=${fieldBlob.size}`);

    const response = await fetch(`${API_BASE}/api/upload-to-r2`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sessionId,
        mosId,
        portraitBase64,
        fieldBase64,
      }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Compressed upload error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to upload images',
    };
  }
}

export async function uploadImagesToR2(
  sessionId: string,
  portraitImage: string,
  fieldImage: string,
  mosId?: string
): Promise<UploadImagesResponse> {
  console.log('[R2 Upload v2.0] Starting upload process...');

  try {
    // Detect if images are base64 (from Gemini) or URLs (legacy)
    const isBase64 = portraitImage.startsWith('data:') || !portraitImage.startsWith('http');
    console.log('[R2 Upload v2.0] Image type:', isBase64 ? 'base64' : 'URL');

    if (isBase64) {
      // Try presigned URL upload first (bypasses Vercel payload limits)
      console.log('[R2 Upload v2.0] Attempting presigned URL upload...');

      try {
        const [portraitUrl, fieldUrl] = await Promise.all([
          uploadImageDirect(sessionId, portraitImage, 'portrait', mosId),
          uploadImageDirect(sessionId, fieldImage, 'field', mosId),
        ]);

        // If presigned URL upload succeeded
        if (portraitUrl || fieldUrl) {
          console.log('Presigned URL upload successful');
          // Update session with the R2 URLs
          await updateSession(sessionId, {
            portraitPhotoUrl: portraitUrl || undefined,
            fieldPhotoUrl: fieldUrl || undefined,
            status: 'completed',
          });

          return {
            success: true,
            portraitUrl: portraitUrl || undefined,
            fieldUrl: fieldUrl || undefined,
          };
        }
      } catch (directError) {
        console.log('Presigned URL upload failed, trying compressed server upload...', directError);
      }

      // Fallback: Use compressed server upload
      console.log('Falling back to compressed server upload...');
      return await uploadImageViaServer(sessionId, portraitImage, fieldImage, mosId);
    } else {
      // Legacy URL params - use existing upload-to-r2 endpoint
      const body: Record<string, string | undefined> = {
        sessionId,
        mosId,
        portraitUrl: portraitImage,
        fieldUrl: fieldImage,
      };

      const response = await fetch(`${API_BASE}/api/upload-to-r2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.error('Upload to R2 error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to upload images',
    };
  }
}

export interface SubmitEmailResponse {
  success: boolean;
  error?: string;
}

export async function submitEmail(
  sessionId: string,
  email: string
): Promise<SubmitEmailResponse> {
  try {
    const response = await fetch(`${API_BASE}/api/session-by-id?sessionId=${sessionId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();
    return { success: data.success, error: data.error };
  } catch (error) {
    console.error('Submit email error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to submit email',
    };
  }
}

export interface LogMOSViewResponse {
  success: boolean;
  error?: string;
}

export async function logMOSView(
  sessionId: string,
  mosId: string,
  mosTitle: string,
  isInitial: boolean
): Promise<LogMOSViewResponse> {
  try {
    const response = await fetch(`${API_BASE}/api/session-by-id?sessionId=${sessionId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        addMOSView: {
          mosId,
          mosTitle,
          isInitial,
        },
      }),
    });

    const data = await response.json();
    return { success: data.success, error: data.error };
  } catch (error) {
    console.error('Log MOS view error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to log MOS view',
    };
  }
}
